import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import ReactMarkdown from 'react-markdown';

// Simple region mapping for countries
const getCountryRegion = (countryName: string): string => {
  const regionMap: { [key: string]: string } = {
    // Europe
    'Austria': 'Europe', 'Germany': 'Europe', 'France': 'Europe', 'Italy': 'Europe', 'Spain': 'Europe',
    'United Kingdom': 'Europe', 'Netherlands': 'Europe', 'Belgium': 'Europe', 'Switzerland': 'Europe',
    'Sweden': 'Europe', 'Norway': 'Europe', 'Denmark': 'Europe', 'Finland': 'Europe', 'Poland': 'Europe',
    'Czech Republic': 'Europe', 'Hungary': 'Europe', 'Romania': 'Europe', 'Bulgaria': 'Europe',
    'Greece': 'Europe', 'Portugal': 'Europe', 'Ireland': 'Europe', 'Croatia': 'Europe', 'Slovenia': 'Europe',
    'Slovakia': 'Europe', 'Lithuania': 'Europe', 'Latvia': 'Europe', 'Estonia': 'Europe', 'Luxembourg': 'Europe',
    'Malta': 'Europe', 'Cyprus': 'Europe', 'Iceland': 'Europe', 'Albania': 'Europe', 'Serbia': 'Europe',
    'Montenegro': 'Europe', 'Bosnia and Herzegovina': 'Europe', 'North Macedonia': 'Europe', 'Kosovo': 'Europe',
    'Moldova': 'Europe', 'Ukraine': 'Europe', 'Belarus': 'Europe', 'Georgia': 'Europe', 'Armenia': 'Europe',
    'Azerbaijan': 'Europe', 'Russia': 'Europe', 'Turkey': 'Europe', 'Andorra': 'Europe', 'Monaco': 'Europe',
    'San Marino': 'Europe', 'Vatican City': 'Europe', 'Liechtenstein': 'Europe',

    // Asia
    'China': 'Asia', 'Japan': 'Asia', 'South Korea': 'Asia', 'India': 'Asia', 'Indonesia': 'Asia', 'Thailand': 'Asia',
    'Vietnam': 'Asia', 'Malaysia': 'Asia', 'Singapore': 'Asia', 'Philippines': 'Asia', 'Myanmar': 'Asia',
    'Cambodia': 'Asia', 'Laos': 'Asia', 'Brunei': 'Asia', 'Mongolia': 'Asia', 'Kazakhstan': 'Asia',
    'Uzbekistan': 'Asia', 'Kyrgyzstan': 'Asia', 'Tajikistan': 'Asia', 'Turkmenistan': 'Asia', 'Afghanistan': 'Asia',
    'Pakistan': 'Asia', 'Bangladesh': 'Asia', 'Sri Lanka': 'Asia', 'Nepal': 'Asia', 'Bhutan': 'Asia',
    'Maldives': 'Asia', 'Iran': 'Asia', 'Iraq': 'Asia', 'Saudi Arabia': 'Asia', 'Yemen': 'Asia',
    'Oman': 'Asia', 'United Arab Emirates': 'Asia', 'Qatar': 'Asia', 'Kuwait': 'Asia', 'Bahrain': 'Asia',
    'Jordan': 'Asia', 'Lebanon': 'Asia', 'Syria': 'Asia', 'Israel': 'Asia', 'Palestine': 'Asia',
    'North Korea': 'Asia', 'Taiwan': 'Asia', 'Timor-Leste': 'Asia',

    // North America
    'United States': 'North America', 'Canada': 'North America', 'Mexico': 'North America',
    'Guatemala': 'North America', 'Belize': 'North America', 'El Salvador': 'North America',
    'Honduras': 'North America', 'Nicaragua': 'North America', 'Costa Rica': 'North America',
    'Panama': 'North America', 'Cuba': 'North America', 'Jamaica': 'North America', 'Haiti': 'North America',
    'Dominican Republic': 'North America', 'Bahamas': 'North America', 'Barbados': 'North America',
    'Trinidad and Tobago': 'North America', 'Grenada': 'North America', 'Saint Lucia': 'North America',
    'Saint Vincent and the Grenadines': 'North America', 'Antigua and Barbuda': 'North America',
    'Saint Kitts and Nevis': 'North America', 'Dominica': 'North America',

    // South America
    'Brazil': 'South America', 'Argentina': 'South America', 'Chile': 'South America', 'Colombia': 'South America',
    'Peru': 'South America', 'Venezuela': 'South America', 'Ecuador': 'South America', 'Bolivia': 'South America',
    'Paraguay': 'South America', 'Uruguay': 'South America', 'Guyana': 'South America', 'Suriname': 'South America',

    // Africa
    'Algeria': 'Africa', 'Egypt': 'Africa', 'Morocco': 'Africa', 'Tunisia': 'Africa', 'Libya': 'Africa',
    'Sudan': 'Africa', 'South Sudan': 'Africa', 'Ethiopia': 'Africa', 'Somalia': 'Africa', 'Kenya': 'Africa',
    'Tanzania': 'Africa', 'Uganda': 'Africa', 'Rwanda': 'Africa', 'Burundi': 'Africa', 'Democratic Republic of the Congo': 'Africa',
    'Congo': 'Africa', 'Central African Republic': 'Africa', 'Chad': 'Africa', 'Cameroon': 'Africa',
    'Nigeria': 'Africa', 'Niger': 'Africa', 'Mali': 'Africa', 'Burkina Faso': 'Africa', 'Senegal': 'Africa',
    'Gambia': 'Africa', 'Guinea-Bissau': 'Africa', 'Guinea': 'Africa', 'Sierra Leone': 'Africa',
    'Liberia': 'Africa', 'Côte d\'Ivoire': 'Africa', 'Ghana': 'Africa', 'Togo': 'Africa', 'Benin': 'Africa',
    'Gabon': 'Africa', 'Equatorial Guinea': 'Africa', 'São Tomé and Príncipe': 'Africa', 'Angola': 'Africa',
    'Zambia': 'Africa', 'Malawi': 'Africa', 'Mozambique': 'Africa', 'Zimbabwe': 'Africa', 'Botswana': 'Africa',
    'Namibia': 'Africa', 'South Africa': 'Africa', 'Lesotho': 'Africa', 'Eswatini': 'Africa', 'Madagascar': 'Africa',
    'Mauritius': 'Africa', 'Seychelles': 'Africa', 'Comoros': 'Africa', 'Djibouti': 'Africa', 'Eritrea': 'Africa',
    'Cape Verde': 'Africa', 'Mauritania': 'Africa',

    // Oceania
    'Australia': 'Oceania', 'New Zealand': 'Oceania', 'Papua New Guinea': 'Oceania', 'Fiji': 'Oceania',
    'Solomon Islands': 'Oceania', 'Vanuatu': 'Oceania', 'New Caledonia': 'Oceania', 'Samoa': 'Oceania',
    'Tonga': 'Oceania', 'Kiribati': 'Oceania', 'Micronesia': 'Oceania', 'Marshall Islands': 'Oceania',
    'Palau': 'Oceania', 'Nauru': 'Oceania', 'Tuvalu': 'Oceania'
  };
  
  return regionMap[countryName] || 'Other';
};

// Declare process for TypeScript
declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

// Types
interface Company {
  name: string;
  industry?: string;
  size?: string;
  uniqueCode?: string;
}

interface CountryVolume {
  country: string;
  ap_volume: number; // Accounts Payable
  ar_volume: number; // Accounts Receivable
  domestic_percentage: number; // Percentage of domestic invoices (0-100)
  revenue: number; // Annual revenue in local currency
  currency: string; // ISO 4217 currency code for revenue
  legal_entities_count: number; // Number of legal entities
}

interface ResearchRequest {
  company: Company;
  countries: CountryVolume[];
  research_mode: 'manual' | 'auto' | 'review';
}

interface ComplianceRequirement {
  title: string;
  description: string;
  deadline?: string;
  penalty?: string;
  status: string;
}

interface MandateStatus {
  b2b: string; // "compliant", "non_compliant", "unknown"
  b2g: string;
  b2c: string;
}

interface CountryOperation {
  country: string;
  ap_volume: number;
  ar_volume: number;
  mandate_status: MandateStatus;
  risk_of_non_compliance?: number;
  risk_currency: string;
}

interface TimelineEvent {
  country: string;
  event_type: string; // "deadline", "implementation", "review"
  title: string;
  description: string;
  date: string;
  priority: string; // "high", "medium", "low"
  relevance?: string[]; // ["b2b", "b2c", "b2g"]
  summary?: string; // Detailed description from JSON
  sources?: string[]; // URLs from the research
}

interface ExecutiveSummary {
  company_name: string;
  total_risk_non_compliance: number;
  total_countries: number;
  total_b2b_mandates: number;
  total_b2g_mandates: number;
  total_b2c_mandates: number;
  risk_currency: string;
}

interface ComplianceReport {
  id: string;
  company: Company;
  countries: CountryVolume[];
  generated_at: string;
  requirements: ComplianceRequirement[];
  summary: string;
  risk_level: string;
  next_deadline?: string;
  total_impact: {
    ap_invoices: number;
    ar_invoices: number;
    estimated_penalties: string;
  };
  // New enhanced fields
  executive_summary: ExecutiveSummary;
  timeline_events: TimelineEvent[];
  country_operations: CountryOperation[];
}

// Generate unique code with 8 random characters (used as report ID)
const generateUniqueCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ123456789'; // Removed I, O, 0 to avoid confusion
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Helper function to get API URL
const getApiUrl = (): string => {
  // Prefer window.API_URL if set (from config.js or env)
  if (typeof window !== 'undefined' && (window as any).API_URL) {
    return (window as any).API_URL;
  }
  // Fallback to production URL
  return "https://atlas-api.max-borgmann.com";
};

// Step Navigation Component
function StepNavigation({ currentStep }: { currentStep: number }) {
  const steps = [
    { id: 1, name: 'Company Profile', description: 'Tell us about your business' },
    { id: 2, name: 'Volume Analysis', description: 'Define your invoice volumes' },
    { id: 3, name: 'Compliance Report', description: 'View your personalized report' }
  ];

  // Get company data to display unique code
  const companyData = localStorage.getItem('atlas_company');
  const company = companyData ? JSON.parse(companyData) : null;

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between h-auto sm:h-16 py-4 sm:py-0 items-center">
          {/* Header Section */}
          <div className="flex items-center justify-between sm:justify-start mb-4 sm:mb-0">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-gray-900">Atlas</h1>
            </div>
            {company?.uniqueCode && (
              <div className="ml-4 sm:ml-6 flex items-center">
                <span className="text-xs sm:text-sm text-gray-500 mr-2">Code:</span>
                <a
                  href={`${window.location.origin}/report/${company.uniqueCode}`}
                  className="font-mono text-xs sm:text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 hover:text-blue-800 transition-colors"
                >
                  {company.uniqueCode}
                </a>
              </div>
            )}
          </div>

          {/* Step Navigation Section */}
          <div className="w-full sm:w-auto">
            {/* Mobile: Show current step only */}
            <div className="sm:hidden">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-blue-600 text-white">
                    {currentStep}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-600">
                      {steps[currentStep - 1]?.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      Step {currentStep} of {steps.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop: Show all steps */}
            <div className="hidden sm:flex items-center space-x-8">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center ${index < steps.length - 1 ? 'mr-8' : ''}`}>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= step.id 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {step.id}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${
                        currentStep >= step.id ? 'text-blue-600' : 'text-gray-500'
                      }`}>
                        {step.name}
                      </p>
                      <p className="text-xs text-gray-400">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 ${
                      currentStep > step.id ? 'bg-blue-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

// Company Onboarding Component
function CompanyOnboarding() {
  const [company, setCompany] = useState<Company>({
    name: '',
    industry: '',
    size: '',
    uniqueCode: generateUniqueCode()
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company.name.trim()) {
      setError('Please enter your company name');
      return;
    }
    
    // Store company info and navigate to volume input
    localStorage.setItem('atlas_company', JSON.stringify(company));
    navigate('/volumes');
  };

  const handleIconClick = () => {
    setCompany({
      name: 'Example Ltd.',
      industry: 'technology',
      size: 'large',
      uniqueCode: company.uniqueCode
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <StepNavigation currentStep={1} />
      
      <div className="max-w-full mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="max-w-full mx-auto">
            <div className="text-center mb-6 sm:mb-8">
              <div 
                className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-green-200 transition-colors"
                onClick={handleIconClick}
                title="Click to fill with example data"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Company Profile
              </h2>
              <p className="text-gray-600 text-sm sm:text-base">
                Tell us about your business to personalize your compliance analysis
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              <div>
                <label htmlFor="company_name" className="block text-sm font-semibold text-gray-700 mb-3">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company_name"
                  value={company.name}
                  onChange={(e) => setCompany({ ...company, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                  placeholder="Enter your company name"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-3">
                    Industry
                  </label>
                  <select
                    id="industry"
                    value={company.industry}
                    onChange={(e) => setCompany({ ...company, industry: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                  >
                    <option value="">Select industry</option>
                    <option value="technology">Technology</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance</option>
                    <option value="consulting">Consulting</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="size" className="block text-sm font-semibold text-gray-700 mb-3">
                    Company Size
                  </label>
                  <select
                    id="size"
                    value={company.size}
                    onChange={(e) => setCompany({ ...company, size: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                  >
                    <option value="">Select size</option>
                    <option value="startup">Startup (1-50 employees)</option>
                    <option value="small">Small (51-200 employees)</option>
                    <option value="medium">Medium (201-1000 employees)</option>
                    <option value="large">Large (1000+ employees)</option>
                  </select>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="pt-4 sm:pt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 sm:py-4 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 font-semibold text-base sm:text-lg"
                >
                  {loading ? 'Processing...' : 'Continue to Volume Analysis'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// Volume Input Component
function VolumeInput() {
  const [countries, setCountries] = useState<CountryVolume[]>([
    { country: '', ap_volume: 0, ar_volume: 0, domestic_percentage: 0, revenue: 0, currency: '', legal_entities_count: 0 }
  ]);
  const [countrySearch, setCountrySearch] = useState<string[]>(['']);
  const [showCountryDropdown, setShowCountryDropdown] = useState<boolean[]>([false]);
  const [currencySearch, setCurrencySearch] = useState<string[]>(['']);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState<boolean[]>([false]);
  const [researchMode, setResearchMode] = useState<'manual' | 'auto' | 'review' | 'csv'>('manual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiResearchResults, setAiResearchResults] = useState<CountryVolume[]>([]);
  const [showResearchResults, setShowResearchResults] = useState(false);
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [csvError, setCsvError] = useState('');
  const [csvUploading, setCsvUploading] = useState(false);

  // AI Research with Perplexity API
  const performAIResearch = async (companyName: string): Promise<CountryVolume[]> => {
    try {
      // Real API call
      console.log('Making API call to:', `${getApiUrl()}/api/research-company`);
      const response = await fetch(`${getApiUrl()}/api/research-company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: companyName,
          research_prompt: `Research the global operations and invoice volumes for ${companyName}`
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`Research request failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Response data:', data);
      
      // Validate and normalize the returned data to ensure all fields are present
      const countries = data.countries || [];
      return countries.map((country: any) => ({
        country: country.country || '',
        ap_volume: typeof country.ap_volume === 'number' ? country.ap_volume : 0,
        ar_volume: typeof country.ar_volume === 'number' ? country.ar_volume : 0,
        domestic_percentage: typeof country.domestic_percentage === 'number' ? country.domestic_percentage : 0,
        revenue: typeof country.revenue === 'number' ? country.revenue : 0,
        currency: typeof country.currency === 'string' ? country.currency : '',
        legal_entities_count: typeof country.legal_entities_count === 'number' ? country.legal_entities_count : 0
      }));
    } catch (error) {
      console.error('AI Research error:', error);
      throw new Error('Failed to perform AI research. Please try manual entry instead.');
    }
  };

  // All 196 countries of the world
  const allCountries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
    "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
    "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
    "Fiji", "Finland", "France",
    "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
    "Haiti", "Honduras", "Hungary",
    "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast",
    "Jamaica", "Japan", "Jordan",
    "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
    "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
    "Oman",
    "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
    "Qatar",
    "Romania", "Russia", "Rwanda",
    "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
    "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
    "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
    "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
    "Yemen",
    "Zambia", "Zimbabwe"
  ];

  // All available currencies for the dropdown
  const allCurrencies = Array.from(new Set([
    'EUR', 'USD', 'GBP', 'NOK', 'SEK', 'DKK', 'CHF', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'TRY', 'RUB', 'UAH', 'JPY', 'CNY', 'INR', 'BRL', 'MXN', 'AUD', 'CAD', 'ZAR', 'SGD', 'HKD', 'NZD', 'KRW', 'IDR', 'MYR', 'THB', 'ILS', 'SAR', 'AED', 'KWD', 'QAR', 'CLP', 'COP', 'PEN', 'ARS', 'VND', 'PHP', 'PKR', 'EGP', 'NGN', 'KES', 'GHS', 'TND', 'MAD', 'DZD', 'TWD', 'ISK', 'HRK', 'LTL', 'LVL', 'EEK', 'SKK', 'SIT', 'MTL', 'CYP', 'TRL', 'EEK', 'BYR', 'AZN', 'GEL', 'AMD', 'MKD', 'ALL', 'BAM', 'MDL', 'RSD', 'MZN', 'AOA', 'TZS', 'UGX', 'ZMW', 'BWP', 'NAD', 'SZL', 'LSL', 'MWK', 'MUR', 'SCR', 'MGA', 'KMF', 'DJF', 'SOS', 'SDG', 'SSP', 'LYD', 'TND', 'MRU', 'GMD', 'SLL', 'LRD', 'CVE', 'GNF', 'XOF', 'XAF', 'XPF', 'FJD', 'PGK', 'SBD', 'VUV', 'TOP', 'WST', 'KID', 'BND', 'MMK', 'LAK', 'KHR', 'MOP', 'KZT', 'UZS', 'TJS', 'KGS', 'MNT', 'AFN', 'IRR', 'IQD', 'SYP', 'JOD', 'LBP', 'YER', 'OMR', 'BHD', 'MVR', 'LKR', 'NPR', 'BTN', 'BDT', 'MMK', 'THB', 'VND', 'IDR', 'MYR', 'SGD', 'PHP', 'CNY', 'TWD', 'HKD', 'KRW', 'JPY', 'MOP', 'KPW', 'NZD', 'AUD', 'FJD', 'PGK', 'SBD', 'VUV', 'TOP', 'WST', 'KID', 'BND', 'USD', 'CAD', 'MXN', 'GTQ', 'HNL', 'NIO', 'CRC', 'PAB', 'CUP', 'DOP', 'JMD', 'TTD', 'BSD', 'BBD', 'BZD', 'GYD', 'SRD', 'XCD', 'BMD', 'KYD', 'ANG', 'AWG', 'HTG', 'XPF', 'CLP', 'COP', 'PEN', 'BOB', 'PYG', 'UYU', 'VEF', 'BRL', 'ARS', 'GHS', 'NGN', 'ZAR', 'EGP', 'MAD', 'TND', 'DZD', 'LYD', 'SDG', 'SSP', 'MRO', 'GMD', 'SLL', 'LRD', 'CVE', 'GNF', 'XOF', 'XAF', 'XPF'
  ]));

  const addCountry = () => {
    const newCountry: CountryVolume = {
      country: '',
      ap_volume: 0,
      ar_volume: 0,
      domestic_percentage: 0,
      revenue: 0,
      currency: '',
      legal_entities_count: 0
    };
    setCountries([...countries, newCountry]);
    setCountrySearch([...countrySearch, '']);
    setShowCountryDropdown([...showCountryDropdown, false]);
    setCurrencySearch([...currencySearch, '']);
    setShowCurrencyDropdown([...showCurrencyDropdown, false]);
    
    // Scroll to the newly added country after a brief delay
    setTimeout(() => {
      const newCountryElement = document.querySelector(`[data-country-index="${countries.length}"]`);
      if (newCountryElement) {
        newCountryElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Focus on the country search input
        const searchInput = newCountryElement.querySelector('input[type="text"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }
    }, 100);
  };

  const removeCountry = (index: number) => {
    if (countries.length > 1) {
      setCountries(countries.filter((_, i) => i !== index));
      setCountrySearch(countrySearch.filter((_, i) => i !== index));
      setShowCountryDropdown(showCountryDropdown.filter((_, i) => i !== index));
      setCurrencySearch(currencySearch.filter((_, i) => i !== index));
      setShowCurrencyDropdown(showCurrencyDropdown.filter((_, i) => i !== index));
    }
  };

  const formatNumber = (value: string): string => {
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, '');
    // Add thousand separators
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const parseNumber = (value: string): number => {
    // Remove all non-digit characters and convert to number
    return parseInt(value.replace(/\D/g, '')) || 0;
  };

  const updateCountry = (index: number, field: keyof CountryVolume, value: string | number) => {
    const updated = [...countries];
    if (field === 'country') {
      updated[index] = { ...updated[index], [field]: value as string };
    } else if (field === 'domestic_percentage' || field === 'legal_entities_count') {
      // For domestic percentage and legal entities count, value is already a number
      updated[index] = { ...updated[index], [field]: value as number };
    } else {
      // For volume fields, store the numeric value but display formatted
      updated[index] = { ...updated[index], [field]: parseNumber(value as string) };
    }
    setCountries(updated);
  };

  const handleCountrySearch = (index: number, searchTerm: string) => {
    const updatedSearch = [...countrySearch];
    updatedSearch[index] = searchTerm;
    setCountrySearch(updatedSearch);

    const updatedDropdown = [...showCountryDropdown];
    updatedDropdown[index] = searchTerm.length > 0;
    setShowCountryDropdown(updatedDropdown);
  };

  const selectCountry = (index: number, country: string) => {
    updateCountry(index, 'country', country);
    const updatedSearch = [...countrySearch];
    updatedSearch[index] = '';
    setCountrySearch(updatedSearch);
    
    const updatedDropdown = [...showCountryDropdown];
    updatedDropdown[index] = false;
    setShowCountryDropdown(updatedDropdown);
  };

  const editCountry = (index: number) => {
    // Clear the selected country
    const updatedCountries = [...countries];
    updatedCountries[index] = { ...updatedCountries[index], country: '' };
    setCountries(updatedCountries);
    
    // Clear search and show dropdown
    const updatedSearch = [...countrySearch];
    updatedSearch[index] = '';
    setCountrySearch(updatedSearch);
    
    const updatedDropdown = [...showCountryDropdown];
    updatedDropdown[index] = true;
    setShowCountryDropdown(updatedDropdown);
    
    // Focus the input field after a short delay to ensure DOM update
    setTimeout(() => {
      if (inputRefs.current[index]) {
        inputRefs.current[index]?.focus();
      }
    }, 10);
  };

  const filteredCountries = (index: number) => {
    const searchTerm = countrySearch[index].toLowerCase();
    return allCountries.filter(country => 
      country.toLowerCase().includes(searchTerm)
    ).slice(0, 10); // Limit to 10 results for better UX
  };

  const filteredCurrencies = (index: number) => {
    const searchTerm = currencySearch[index]?.toUpperCase() || '';
    return allCurrencies.filter(code => code.includes(searchTerm)).slice(0, 10);
  };

  const handleCurrencySearch = (index: number, searchTerm: string) => {
    const updatedSearch = [...currencySearch];
    updatedSearch[index] = searchTerm.toUpperCase();
    setCurrencySearch(updatedSearch);

    const updatedDropdown = [...showCurrencyDropdown];
    updatedDropdown[index] = searchTerm.length > 0;
    setShowCurrencyDropdown(updatedDropdown);
  };

  const selectCurrency = (index: number, currency: string) => {
    const updatedCountries = [...countries];
    updatedCountries[index] = { ...updatedCountries[index], currency };
    setCountries(updatedCountries);
    
    const updatedSearch = [...currencySearch];
    updatedSearch[index] = '';
    setCurrencySearch(updatedSearch);
    
    const updatedDropdown = [...showCurrencyDropdown];
    updatedDropdown[index] = false;
    setShowCurrencyDropdown(updatedDropdown);
  };

  const handleAutoResearch = async () => {
    setLoading(true);
    setError('');
    
    try {
      const companyData = localStorage.getItem('atlas_company');
      const company = companyData ? JSON.parse(companyData) : null;
      const results = await performAIResearch(company?.name || '');
      setAiResearchResults(results);
      setCountries(results); // Set the countries state with AI results
      setCountrySearch(results.map(() => '')); // Initialize search terms
      setShowCountryDropdown(results.map(() => false)); // Initialize dropdown states
      setCurrencySearch(results.map(() => '')); // Initialize currency search terms
      setShowCurrencyDropdown(results.map(() => false)); // Initialize currency dropdown states
      setResearchMode('manual'); // Switch to manual entry mode
      setShowResearchResults(false); // Hide the review/mockup section
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform AI research');
    } finally {
      setLoading(false);
    }
  };

  const handleIconClick = () => {
    const exampleCountries: CountryVolume[] = [
      { country: 'Germany', ap_volume: 40000, ar_volume: 90000, domestic_percentage: 85, revenue: 5000000, currency: 'EUR', legal_entities_count: 12 },
      { country: 'France', ap_volume: 25000, ar_volume: 50000, domestic_percentage: 80, revenue: 3500000, currency: 'EUR', legal_entities_count: 8 },
      { country: 'Poland', ap_volume: 7500, ar_volume: 2000, domestic_percentage: 75, revenue: 800000, currency: 'PLN', legal_entities_count: 3 },
      { country: 'Spain', ap_volume: 8000, ar_volume: 17000, domestic_percentage: 70, revenue: 1200000, currency: 'EUR', legal_entities_count: 5 },
      { country: 'Italy', ap_volume: 12000, ar_volume: 67000, domestic_percentage: 75, revenue: 2800000, currency: 'EUR', legal_entities_count: 7 },
      { country: 'Norway', ap_volume: 2500, ar_volume: 12000, domestic_percentage: 65, revenue: 1500000, currency: 'NOK', legal_entities_count: 4 },
      { country: 'United Kingdom', ap_volume: 5000, ar_volume: 40000, domestic_percentage: 60, revenue: 2200000, currency: 'GBP', legal_entities_count: 6 }
    ];
    
    setCountries(exampleCountries);
    setCountrySearch(exampleCountries.map(() => ''));
    setShowCountryDropdown(exampleCountries.map(() => false));
    setCurrencySearch(exampleCountries.map(() => ''));
    setShowCurrencyDropdown(exampleCountries.map(() => false));
    setResearchMode('manual');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get company data
      const companyData = localStorage.getItem('atlas_company');
      if (!companyData) {
        setError('Company information not found. Please start over.');
        return;
      }
      const company = JSON.parse(companyData);

      let finalCountries: CountryVolume[] = [];

      if (researchMode === 'auto') {
        // Perform AI research
        try {
          const researchResults = await performAIResearch(company.name);
          if (researchResults.length > 0) {
            finalCountries = researchResults;
            setAiResearchResults(researchResults);
          } else {
            throw new Error('No research results found');
          }
        } catch (researchError) {
          setError('AI research failed. Please try manual entry instead.');
          setLoading(false);
          return;
        }
      } else {
        // Manual entry validation
        const validCountries = countries.filter(c => c.country.trim() !== '');
        if (validCountries.length === 0) {
          setError('Please add at least one country with invoice volumes.');
          setLoading(false);
          return;
        }
        finalCountries = validCountries;
      }

      // Create research request
      const researchRequest: ResearchRequest = {
        company,
        countries: finalCountries,
        research_mode: researchMode
      };

      // Call backend API
      const response = await fetch(`${getApiUrl()}/api/generate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(researchRequest),
      });

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const report = await response.json();
      
      // Store report and navigate
      localStorage.setItem('atlas_report', JSON.stringify(report));
      navigate(`/report/${report.id}`);
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // CSV parsing helper
  const parseCsv = (csvText: string): CountryVolume[] => {
    const lines = csvText.trim().split('\n');
    const headers = lines[0].split(',');
    const rows = lines.slice(1);
    return rows.map(row => {
      const cols = row.split(',');
      return {
        country: cols[0] || '',
        legal_entities_count: Number(cols[1]) || 0,
        ap_volume: Number(cols[2]) || 0,
        ar_volume: Number(cols[3]) || 0,
        domestic_percentage: Number(cols[4]) || 0,
        revenue: Number(cols[5]) || 0,
        currency: cols[6] || ''
      };
    });
  };

  if (showResearchResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <StepNavigation currentStep={3} />
        <div className="max-w-full mx-auto px-4 py-8 sm:py-12">
          <div className="mt-12">
            <h2 className="text-3xl font-bold mb-6 text-center">Compliance Report Overview</h2>
            <Timeline countries={mockMandates} />
            <MapPlaceholder countries={mockMandates} />
            <CountryList countries={mockMandates} onSelect={setSelectedCountry} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <StepNavigation currentStep={2} />
      
      <div className="max-w-full mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="max-w-full mx-auto">
            <div className="text-center mb-6 sm:mb-8">
              <div 
                className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-green-200 transition-colors"
                onClick={handleIconClick}
                title="Click to fill with example data"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                How would you like to provide volume data?
              </h2>
              <p className="text-gray-600 text-sm sm:text-base">
                Choose between manual entry or AI-powered research to estimate your invoice volumes
              </p>
            </div>

            <div className="space-y-6 sm:space-y-8">
              {/* Mode Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Manual Entry */}
                <button
                  type="button"
                  onClick={() => setResearchMode('manual')}
                  className={`p-6 sm:p-8 border-2 rounded-xl text-left transition-all transform hover:scale-105 ${
                    researchMode === 'manual'
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center mb-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mr-3 sm:mr-4 ${
                      researchMode === 'manual' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${researchMode === 'manual' ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg">Manual Entry</h3>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">I know my invoice volumes and want to enter them manually for precise analysis.</p>
                </button>
                {/* AI Research */}
                <button
                  type="button"
                  onClick={() => setResearchMode('auto')}
                  className={`p-6 sm:p-8 border-2 rounded-xl text-left transition-all transform hover:scale-105 ${
                    researchMode === 'auto'
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center mb-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mr-3 sm:mr-4 ${
                      researchMode === 'auto' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${researchMode === 'auto' ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg">AI Research</h3>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">I don't have this data - please research and estimate for me using AI analysis.</p>
                </button>
                {/* CSV Upload */}
                <button
                  type="button"
                  onClick={() => setResearchMode('csv')}
                  className={`p-6 sm:p-8 border-2 rounded-xl text-left transition-all transform hover:scale-105 ${
                    researchMode === 'csv'
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center mb-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center mr-3 sm:mr-4 ${
                      researchMode === 'csv' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${researchMode === 'csv' ? 'text-blue-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-base sm:text-lg">CSV Upload</h3>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base mb-4">
                    Download the <a href="/VolumeCsvTemplate.csv" download className="text-blue-600 underline">CSV template</a> and fill in your data. Then upload your completed file below.
                  </p>
                </button>
              </div>

              {/* Manual Entry Form */}
              {researchMode === 'manual' && (
                <div className="space-y-6 sm:space-y-8">
                  <div className="text-center">
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                      Manual Volume Entry
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base">
                      Enter your yearly invoice volumes for each country where you operate
                    </p>
                  </div>

                  <div className="space-y-4 sm:space-y-6">
                    {countries.map((country, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 sm:p-6" data-country-index={index}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                          <div className="flex items-center mb-3 sm:mb-0">
                            <span className="text-lg sm:text-xl font-semibold text-gray-900 mr-3">
                              {country.country || `Country ${index + 1}`}
                            </span>
                            {countries.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeCountry(index)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-7 gap-1 sm:gap-1 items-end w-full">
                          {/* Country Selection - Wide */}
                          <div className="lg:col-span-2 flex flex-col justify-end h-full">
                            <label className="block text-sm font-semibold text-gray-700 mb-3 min-h-[24px]">
                              Country
                            </label>
                            <div className="relative w-full">
                            {!country.country ? (
                              // Show search input when no country is selected
                              <>
                                <input
                                  type="text"
                                  value={countrySearch[index]}
                                  onChange={(e) => handleCountrySearch(index, e.target.value)}
                                  onFocus={() => {
                                    const updatedDropdown = [...showCountryDropdown];
                                    updatedDropdown[index] = true;
                                    setShowCountryDropdown(updatedDropdown);
                                  }}
                                  placeholder="Search for a country..."
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base align-bottom"
                                  required
                                  ref={(el) => inputRefs.current[index] = el}
                                />
                                {showCountryDropdown[index] && (
                                  <div className="absolute left-0 top-full z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                    {filteredCountries(index).map((countryName, countryIndex) => (
                                      <button
                                        key={countryIndex}
                                        type="button"
                                        onClick={() => selectCountry(index, countryName)}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors text-sm sm:text-base"
                                      >
                                        {countryName}
                                      </button>
                                    ))}
                                    {filteredCountries(index).length === 0 && (
                                      <div className="px-4 py-3 text-gray-500 text-sm">
                                        No countries found
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              // Show selected country as bold text
                              <div 
                                onClick={() => editCountry(index)}
                                className="px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-all align-bottom"
                              >
                                <span className="font-semibold text-gray-900">{country.country}</span>
                              </div>
                            )}
                            </div>
                          </div>

                          {/* Number of Legal Entities - Small */}
                          <div className="lg:col-span-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-3 min-h-[24px]">
                              Legal Entities
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={!country.legal_entities_count && country.legal_entities_count !== 0 ? '' : (country.legal_entities_count || 0)}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                                updateCountry(index, 'legal_entities_count', value);
                              }}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                              placeholder="0"
                            />
                          </div>

                          {/* AP Invoices - Small */}
                          <div className="lg:col-span-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-3 min-h-[24px]">
                              AP Invoices/Year
                            </label>
                            <input
                              type="text"
                              value={!country.ap_volume || country.ap_volume === 0 ? '' : formatNumber((country.ap_volume || 0).toString())}
                              onChange={(e) => updateCountry(index, 'ap_volume', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-all text-base"
                              placeholder="0"
                            />
                          </div>

                          {/* AR Invoices - Small */}
                          <div className="lg:col-span-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-3 min-h-[24px]">
                              AR Invoices/Year
                            </label>
                            <input
                              type="text"
                              value={!country.ar_volume || country.ar_volume === 0 ? '' : formatNumber((country.ar_volume || 0).toString())}
                              onChange={(e) => updateCountry(index, 'ar_volume', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-all text-base"
                              placeholder="0"
                            />
                          </div>

                          {/* % Domestic Invoices - Small */}
                          <div className="lg:col-span-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-3 min-h-[24px]">
                              % Domestic Invoices
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={!country.domestic_percentage && country.domestic_percentage !== 0 ? '' : (country.domestic_percentage || 0)}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                                updateCountry(index, 'domestic_percentage', value);
                              }}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base"
                              placeholder="0"
                            />
                          </div>

                          {/* Revenue & Currency Group - Small */}
                          <div className="w-full min-w-0 lg:col-span-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-3 min-h-[24px] whitespace-nowrap">
                              Revenue (local currency)
                            </label>
                            <div className="flex items-center gap-0 min-w-0">
                              <input
                                type="text"
                                value={!country.revenue || country.revenue === 0 ? '' : formatNumber((country.revenue || 0).toString())}
                                onChange={(e) => updateCountry(index, 'revenue', e.target.value)}
                                className="flex-1 min-w-0 px-4 py-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-all text-base border-r-0 text-right"
                                placeholder="0"
                              />
                              <div className="relative flex-shrink-0">
                                <input
                                  type="text"
                                  value={currencySearch[index] !== undefined && currencySearch[index] !== '' ? currencySearch[index] : country.currency}
                                  onChange={(e) => {
                                    setCurrencySearch(cs => {
                                      const updated = [...cs];
                                      updated[index] = e.target.value.toUpperCase();
                                      return updated;
                                    });
                                    setShowCurrencyDropdown(sd => {
                                      const updated = [...sd];
                                      updated[index] = true;
                                      return updated;
                                    });
                                  }}
                                  onFocus={() => setShowCurrencyDropdown(sd => { const updated = [...sd]; updated[index] = true; return updated; })}
                                  onBlur={() => setTimeout(() => setShowCurrencyDropdown(sd => { const updated = [...sd]; updated[index] = false; return updated; }), 150)}
                                  className="w-16 px-2 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-base uppercase border-l-0 text-center"
                                  maxLength={3}
                                  placeholder="EUR"
                                  autoComplete="off"
                                />
                                {showCurrencyDropdown[index] && (
                                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                    {filteredCurrencies(index).map((currencyCode, currencyIdx) => (
                                      <button
                                        key={currencyIdx}
                                        type="button"
                                        onMouseDown={e => e.preventDefault()}
                                        onClick={() => selectCurrency(index, currencyCode)}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors text-sm sm:text-base"
                                      >
                                        {currencyCode}
                                      </button>
                                    ))}
                                    {filteredCurrencies(index).length === 0 && (
                                      <div className="px-4 py-3 text-gray-500 text-sm">
                                        No currencies found
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Country Button */}
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={addCountry}
                      className="text-blue-600 hover:text-blue-800 font-semibold transition-colors text-base sm:text-lg"
                    >
                      + Add Another Country
                    </button>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-4 sm:pt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setResearchMode('auto');
                        setShowResearchResults(false);
                      }}
                      className="flex-1 sm:flex-none bg-gray-100 text-gray-700 py-3 sm:py-4 px-6 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all font-semibold text-base sm:text-lg"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading || countries.length === 0}
                      className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 sm:py-4 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 font-semibold text-base sm:text-lg"
                    >
                      {loading ? 'Processing...' : 'Generate Report'}
                    </button>
                  </div>
                </div>
              )}

              {/* AI Research Mode */}
              {researchMode === 'auto' && (
                <div className="space-y-6 sm:space-y-8">
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                    AI-Powered Research
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4">
                    Let our AI estimate your invoice volumes for each country where you operate. This will fill in the table below automatically.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-4 sm:pt-6">
                    <button
                      type="button"
                      onClick={() => setResearchMode('manual')}
                      className="flex-1 sm:flex-none bg-gray-100 text-gray-700 py-3 sm:py-4 px-6 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all font-semibold text-base sm:text-lg text-left"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleAutoResearch}
                      disabled={loading}
                      className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 sm:py-4 px-8 rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 font-semibold text-base sm:text-lg text-left"
                    >
                      {loading ? 'Researching...' : 'Start AI Research'}
                    </button>
                  </div>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-4">
                      {error}
                    </div>
                  )}
                </div>
              )}

              {/* CSV Upload Mode */}
              {researchMode === 'csv' && (
                <div className="space-y-6 sm:space-y-8">
                  <div className="text-center">
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                      Upload Volume Data via CSV
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base mb-4">
                      Download the <a href="/VolumeCsvTemplate.csv" download className="text-blue-600 underline">CSV template</a> and fill in your data. Then upload your completed file below.
                    </p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={async (e) => {
                        setCsvError('');
                        setCsvUploading(true);
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const text = await file.text();
                          const parsed = parseCsv(text);
                          if (!parsed.length) throw new Error('No data found in CSV.');
                          setCountries(parsed);
                          setResearchMode('manual');
                        } catch (err: any) {
                          setCsvError('Failed to parse CSV: ' + (err.message || err));
                        } finally {
                          setCsvUploading(false);
                        }
                      }}
                      className="block mx-auto mt-2"
                    />
                    {csvUploading && <div className="text-gray-500 mt-2">Uploading...</div>}
                    {csvError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">{csvError}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Report Page Component (updated to show company info and volumes)
function ReportPage() {
  const { reportCode } = useParams<{ reportCode: string }>();
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/report/${reportCode}`);
        
        if (!response.ok) {
          throw new Error('Report not found');
        }

        const data = await response.json();
        setReport(data);
      } catch (err) {
        setError('Failed to load report. Please check the report code.');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (reportCode) {
      fetchReport();
    }
  }, [reportCode]);

  const copyToClipboard = async () => {
    const url = `${window.location.origin}/report/${reportCode}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg max-w-md">
            <h2 className="text-lg font-semibold mb-2">Error</h2>
            <p>{error}</p>
            <a href="/" className="text-blue-600 hover:underline mt-4 inline-block">
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  const getRiskLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <StepNavigation currentStep={3} />
      
      <div className="max-w-full mx-auto px-4 py-8 sm:py-12">
        <div className="space-y-6 sm:space-y-8">
          {/* Executive Summary */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            {/* Company Information */}
            <div className="mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {report.company.name}
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm sm:text-base text-gray-600 mb-4">
                {report.company.industry && (
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {report.company.industry.charAt(0).toUpperCase() + report.company.industry.slice(1)}
                  </span>
                )}
                {report.company.size && (
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {report.company.size.charAt(0).toUpperCase() + report.company.size.slice(1)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Executive Summary</h3>
            </div>
            
            {/* Enhanced Executive Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-lg sm:text-xl font-bold text-red-600 mb-1">
                  {report.executive_summary.total_risk_non_compliance.toLocaleString()} {report.executive_summary.risk_currency}
                </div>
                <div className="text-xs sm:text-sm text-red-800">Risk Exposure</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-lg sm:text-xl font-bold text-purple-600 mb-1">
                  {report.executive_summary.total_countries}
                </div>
                <div className="text-xs sm:text-sm text-purple-800">Countries</div>
              </div>
            </div>
            
            {/* Mandate Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-lg font-bold text-orange-600 mb-1">
                  {report.executive_summary.total_b2b_mandates}
                </div>
                <div className="text-sm text-orange-800">B2B Mandates</div>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4 text-center">
                <div className="text-lg font-bold text-indigo-600 mb-1">
                  {report.executive_summary.total_b2g_mandates}
                </div>
                <div className="text-sm text-indigo-800">B2G Mandates</div>
              </div>
              <div className="bg-teal-50 rounded-lg p-4 text-center">
                <div className="text-lg font-bold text-teal-600 mb-1">
                  {report.executive_summary.total_b2c_mandates}
                </div>
                <div className="text-sm text-teal-800">B2C Mandates</div>
              </div>
            </div>
            
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
              {report.summary}
            </p>
          </div>

          {/* Timeline Section */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Timeline</h2>
            </div>
            
            <TimelineSection events={report.timeline_events} countries={report.countries} />
          </div>

          {/* Country Operations Section */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Countries of Operation</h2>
            </div>
            
            <CountryOperationsSection operations={report.country_operations} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Mock data for mandates and deadlines
const mockMandates = [
  {
    country: 'Germany',
    ap_volume: 10000,
    ar_volume: 12000,
    status: 'amber',
    next_deadline: '2025-01-01',
    mandates: {
      b2b: { active: true, deadline: '2025-01-01', info: 'B2B e-Invoicing mandatory from 2025-01-01' },
      b2c: { active: false, deadline: null, info: 'No B2C mandate' },
      b2g: { active: true, deadline: '2023-07-01', info: 'B2G e-Invoicing mandatory since 2023-07-01' }
    },
    non_compliance: {
      risk: 'high',
      penalty: 'Up to €50,000 per violation',
      estimated_annual_penalty: '€100,000'
    }
  },
  {
    country: 'France',
    ap_volume: 8000,
    ar_volume: 9500,
    status: 'red',
    next_deadline: '2024-07-01',
    mandates: {
      b2b: { active: true, deadline: '2024-07-01', info: 'B2B e-Invoicing mandatory since 2024-07-01' },
      b2c: { active: false, deadline: null, info: 'No B2C mandate' },
      b2g: { active: true, deadline: '2022-01-01', info: 'B2G e-Invoicing mandatory since 2022-01-01' }
    },
    non_compliance: {
      risk: 'medium',
      penalty: 'Up to €30,000 per violation',
      estimated_annual_penalty: '€60,000'
    }
  },
  {
    country: 'United States',
    ap_volume: 150000,
    ar_volume: 200000,
    status: 'green',
    next_deadline: null,
    mandates: {
      b2b: { active: false, deadline: null, info: 'No B2B e-Invoicing mandate' },
      b2c: { active: false, deadline: null, info: 'No B2C mandate' },
      b2g: { active: false, deadline: null, info: 'No B2G mandate' }
    },
    non_compliance: {
      risk: 'low',
      penalty: 'N/A',
      estimated_annual_penalty: 'N/A'
    }
  }
];

function Timeline({ countries }: { countries: typeof mockMandates }) {
  return (
    <div className="w-full overflow-x-auto py-4">
      <div className="flex space-x-8 items-center">
        {countries.map((c, i) => (
          <div key={c.country} className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-lg mb-2 ${
              c.status === 'green' ? 'bg-green-500' : c.status === 'amber' ? 'bg-yellow-400' : 'bg-red-500'
            }`}>
              {c.country.slice(0,2).toUpperCase()}
            </div>
            <div className="text-xs text-gray-700 font-semibold">{c.country}</div>
            <div className="text-xs text-gray-500">{c.next_deadline ? `Next: ${c.next_deadline}` : 'No deadline'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CountryList({ countries, onSelect }: { countries: typeof mockMandates, onSelect: (c: any) => void }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
      {countries.map((c) => (
        <div key={c.country} className="flex items-center bg-white rounded-lg shadow p-4 cursor-pointer hover:bg-blue-50 transition" onClick={() => onSelect(c)}>
          <span className={`w-4 h-4 rounded-full mr-3 ${
            c.status === 'green' ? 'bg-green-500' : c.status === 'amber' ? 'bg-yellow-400' : 'bg-red-500'
          }`} />
          <span className="font-semibold text-gray-900 flex-1">{c.country}</span>
          <span className="text-xs text-gray-500">AP: {c.ap_volume.toLocaleString()} | AR: {c.ar_volume.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function MapPlaceholder({ countries }: { countries: typeof mockMandates }) {
  return (
    <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-lg mt-8 mb-4">
      [Global Map Placeholder: Countries colored by status]
    </div>
  );
}

function extractMandateSections(content: string) {
  if (!content) return { b2b: '', b2c: '', b2g: '' };
  const b2bMatch = content.match(/=== B2B E-INVOICING MANDATE RESEARCH ===([\s\S]*?)(?=== B2C|=== B2G|$)/);
  const b2cMatch = content.match(/=== B2C E-INVOICING MANDATE RESEARCH ===([\s\S]*?)(?=== B2G|$)/);
  const b2gMatch = content.match(/=== B2G E-INVOICING MANDATE RESEARCH ===([\s\S]*)/);
  return {
    b2b: b2bMatch ? b2bMatch[1].trim() : '',
    b2c: b2cMatch ? b2cMatch[1].trim() : '',
    b2g: b2gMatch ? b2gMatch[1].trim() : '',
  };
}

function CountryDetailModal({ country, onClose }: { country: any, onClose: () => void }) {
  const [researchContent, setResearchContent] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!country) return;
    setResearchContent(null);
    setError(null);
    setLoading(true);
    fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(country.country)}`)
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 404) {
            setResearchContent(null);
            setLoading(false);
            return;
          }
          throw new Error('Failed to fetch research');
        }
        const text = await res.text();
        setResearchContent(text);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [country]);

  const { b2b, b2c, b2g } = extractMandateSections(researchContent || '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        <h2 className="text-2xl font-bold mb-6">{country.country} — E-Invoicing Mandate Research</h2>
        {loading ? (
          <div className="text-center py-12">Loading research...</div>
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : !researchContent ? (
          <div className="text-gray-500">No research available for this country.</div>
        ) : (
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 bg-gray-50 p-4 rounded-lg overflow-y-auto min-w-0 max-h-[400px]">
              <h3 className="font-semibold text-lg mb-2">B2B</h3>
              {b2b ? <ReactMarkdown>{b2b}</ReactMarkdown> : <div className="text-gray-400">No B2B research.</div>}
            </div>
            <div className="flex-1 bg-gray-50 p-4 rounded-lg overflow-y-auto min-w-0 max-h-[400px]">
              <h3 className="font-semibold text-lg mb-2">B2C</h3>
              {b2c ? <ReactMarkdown>{b2c}</ReactMarkdown> : <div className="text-gray-400">No B2C research.</div>}
            </div>
            <div className="flex-1 bg-gray-50 p-4 rounded-lg overflow-y-auto min-w-0 max-h-[400px]">
              <h3 className="font-semibold text-lg mb-2">B2G</h3>
              {b2g ? <ReactMarkdown>{b2g}</ReactMarkdown> : <div className="text-gray-400">No B2G research.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminPage() {
  const [countries, setCountries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [researching, setResearching] = useState<string | null>(null);
  const [researchProgress, setResearchProgress] = useState<{[key: string]: any}>({});
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [researchContent, setResearchContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [collapsedRegions, setCollapsedRegions] = useState<{[key: string]: boolean}>({});
  const [generatingRiskModel, setGeneratingRiskModel] = useState<string | null>(null);
  
  // Risk model modal state
  const [selectedRiskModelCountry, setSelectedRiskModelCountry] = useState<string | null>(null);
  const [riskModelContent, setRiskModelContent] = useState<string>('');
  const [apVolume, setApVolume] = useState<string>('1000');
  const [arVolume, setArVolume] = useState<string>('1000');
  const [calculationResult, setCalculationResult] = useState<any>(null);
  const [calculating, setCalculating] = useState(false);
  
  // Risk model editing state
  const [isEditingRiskModel, setIsEditingRiskModel] = useState(false);
  const [editedRiskModel, setEditedRiskModel] = useState<any>(null);
  const [savingRiskModel, setSavingRiskModel] = useState(false);
  
  // JSON summary editing state
  const [summaryData, setSummaryData] = useState<any>(null);
  const [editedSummary, setEditedSummary] = useState<any>(null);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [activeTab, setActiveTab] = useState<'research' | 'summary'>('research');
  
  const [stats, setStats] = useState<any>({
    total: 0,
    researched: 0,
    pending: 0,
    risk_models_available: 0
  });

  // Group countries by region
  const groupCountriesByRegion = (countries: any[]) => {
    const grouped: { [key: string]: any[] } = {};
    
    countries.forEach(country => {
      const region = getCountryRegion(country.country);
      if (!grouped[region]) {
        grouped[region] = [];
      }
      grouped[region].push(country);
    });
    
    // Sort regions and countries within each region
    const sortedGrouped: { [key: string]: any[] } = {};
    Object.keys(grouped)
      .sort()
      .forEach(region => {
        sortedGrouped[region] = grouped[region].sort((a, b) => a.country.localeCompare(b.country));
      });
    
    return sortedGrouped;
  };

  const toggleRegion = (region: string) => {
    setCollapsedRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };

  React.useEffect(() => {
    fetchCountries();
  }, []);

  // Only add new regions as collapsed, preserve user state
  React.useEffect(() => {
    if (countries.length > 0) {
      const groupedCountries = groupCountriesByRegion(countries);
      setCollapsedRegions(prev => {
        const updated = { ...prev };
      Object.keys(groupedCountries).forEach(region => {
          if (!(region in updated)) {
            updated[region] = true; // Only initialize new regions as collapsed
          }
      });
        return updated;
      });
    }
  }, [countries]);

  // Poll for progress updates when research is in progress
  React.useEffect(() => {
    if (!researching) return;

    const pollProgress = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(researching)}/progress`);
        if (response.ok) {
          const progress = await response.json();
          setResearchProgress(prev => ({ ...prev, [researching]: progress }));
          
          // If research is completed or errored, stop polling and refresh countries
          if (progress.status === 'completed' || progress.status === 'error') {
            setResearching(null);
            await fetchCountries();
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    };

    const interval = setInterval(pollProgress, 2000); // Poll every 2 seconds
    return () => clearInterval(interval);
  }, [researching]);

  const fetchCountries = async () => {
    try {
      setError(null);
      const response = await fetch(`${getApiUrl()}/admin`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setCountries(data.countries || []);
      setStats({
        total: data.total_countries || 0,
        researched: data.researched_countries || 0,
        pending: data.pending_countries || 0,
        risk_models_available: data.risk_models_available || 0
      });
    } catch (error) {
      console.error('Error fetching countries:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch countries');
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const researchCountry = async (country: string) => {
    setResearching(country);
    try {
      const response = await fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(country)}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`Research completed for ${country}:`, result);
        // Refresh the countries list
        await fetchCountries();
      } else {
        const error = await response.json();
        alert(`Research failed for ${country}: ${error.detail}`);
      }
    } catch (error) {
      console.error(`Error researching ${country}:`, error);
      alert(`Research failed for ${country}: ${error}`);
    } finally {
      setResearching(null);
    }
  };

  const generateRiskModel = async (country: string) => {
    setGeneratingRiskModel(country);
    try {
      const response = await fetch(`${getApiUrl()}/admin/risk-model/${encodeURIComponent(country)}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`Risk model generated for ${country}:`, result);
        // alert(`Risk model generated successfully for ${country}!`);
        // Refresh the countries list
        await fetchCountries();
      } else {
        const error = await response.json();
        alert(`Risk model generation failed for ${country}: ${error.detail}`);
      }
    } catch (error) {
      console.error(`Error generating risk model for ${country}:`, error);
      alert(`Risk model generation failed for ${country}: ${error}`);
    } finally {
      setGeneratingRiskModel(null);
    }
  };

  const openResearchModal = async (country: string) => {
    setSelectedCountry(country);
    setIsEditing(false);
    setSaving(false);
    setIsEditingSummary(false);
    setSavingSummary(false);
    setActiveTab('research');
    
    try {
      const response = await fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(country)}`);
      
      if (response.ok) {
        const data = await response.json();
        setResearchContent(data.content);
        setEditedContent(data.content);
      } else if (response.status === 404) {
        setResearchContent('No research available for this country yet. Click "Update Research" to generate research data.');
        setEditedContent('');
      } else {
        const error = await response.json();
        setResearchContent(`Error loading research: ${error.detail}`);
        setEditedContent('');
      }
      
      // Also fetch JSON summary data
      try {
        const summaryResponse = await fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(country)}/summary`);
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setSummaryData(summaryData.summary);
          setEditedSummary(summaryData.summary);
        } else if (summaryResponse.status === 404) {
          setSummaryData(null);
          setEditedSummary(null);
        } else {
          console.error('Error loading summary data:', summaryResponse.status);
          setSummaryData(null);
          setEditedSummary(null);
        }
      } catch (summaryError) {
        console.error('Error fetching summary data:', summaryError);
        setSummaryData(null);
        setEditedSummary(null);
      }
    } catch (error) {
      console.error(`Error loading research for ${country}:`, error);
      setResearchContent(`Error loading research: ${error}`);
      setEditedContent('');
      setSummaryData(null);
      setEditedSummary(null);
    }
  };

  const saveResearch = async () => {
    if (!selectedCountry) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(selectedCountry)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: editedContent })
      });
      
      if (response.ok) {
        setResearchContent(editedContent);
        setIsEditing(false);
        // Refresh the countries list to update timestamps
        await fetchCountries();
      } else {
        const error = await response.json();
        alert(`Failed to save research: ${error.detail}`);
      }
    } catch (error) {
      console.error(`Error saving research for ${selectedCountry}:`, error);
      alert(`Failed to save research: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  const saveSummary = async () => {
    if (!selectedCountry || !editedSummary) return;
    
    setSavingSummary(true);
    try {
      const response = await fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(selectedCountry)}/summary`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: editedSummary
      });
      
      if (response.ok) {
        setSummaryData(editedSummary);
        setIsEditingSummary(false);
        // Refresh the countries list to update timestamps
        await fetchCountries();
      } else {
        const error = await response.json();
        alert(`Failed to save summary: ${error.detail}`);
      }
    } catch (error) {
      console.error(`Error saving summary for ${selectedCountry}:`, error);
      alert(`Failed to save summary: ${error}`);
    } finally {
      setSavingSummary(false);
    }
  };

  const closeModal = () => {
    setSelectedCountry(null);
    setResearchContent('');
    setEditedContent('');
    setIsEditing(false);
    setSaving(false);
    setSummaryData(null);
    setEditedSummary(null);
    setIsEditingSummary(false);
    setSavingSummary(false);
    setActiveTab('research');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const openRiskModelModal = async (country: string) => {
    setSelectedRiskModelCountry(country);
    setCalculationResult(null);
    setApVolume('1000');
    setArVolume('1000');
    setIsEditingRiskModel(false);
    setEditedRiskModel(null);
    
    try {
      const response = await fetch(`${getApiUrl()}/admin/risk-model/${encodeURIComponent(country)}`);
      
      if (response.ok) {
        const data = await response.json();
        setRiskModelContent(data.model_code || 'No model code available');
        
        // Try to parse the JSON data for editing
        try {
          const jsonMatch = data.model_code?.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsedData = JSON.parse(jsonMatch[0]);
            setEditedRiskModel(parsedData);
          }
        } catch (parseError) {
          console.error('Error parsing risk model JSON:', parseError);
          setEditedRiskModel(null);
        }
      } else if (response.status === 404) {
        setRiskModelContent('No risk model available for this country yet. Click "Generate Risk Model" to create one.');
        setEditedRiskModel(null);
      } else {
        const error = await response.json();
        setRiskModelContent(`Error loading risk model: ${error.detail}`);
        setEditedRiskModel(null);
      }
    } catch (error) {
      console.error(`Error loading risk model for ${country}:`, error);
      setRiskModelContent(`Error loading risk model: ${error}`);
      setEditedRiskModel(null);
    }
  };

  const testCalculation = async () => {
    if (!selectedRiskModelCountry) return;
    
    setCalculating(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/calculate-compliance-risk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          country: selectedRiskModelCountry,
          ap_invoices: parseInt(apVolume) || 0,
          ar_invoices: parseInt(arVolume) || 0
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setCalculationResult(result);
      } else {
        const error = await response.json();
        setCalculationResult({ error: error.detail || 'Calculation failed' });
      }
    } catch (error) {
      console.error('Error testing calculation:', error);
      setCalculationResult({ error: `Error testing calculation: ${error}` });
    } finally {
      setCalculating(false);
    }
  };

  const closeRiskModelModal = () => {
    setSelectedRiskModelCountry(null);
    setRiskModelContent('');
    setCalculationResult(null);
    setApVolume('1000');
    setArVolume('1000');
    setCalculating(false);
    setIsEditingRiskModel(false);
    setEditedRiskModel(null);
    setSavingRiskModel(false);
  };

  const saveRiskModel = async () => {
    if (!selectedRiskModelCountry || !editedRiskModel) return;
    
    setSavingRiskModel(true);
    try {
      const response = await fetch(`${getApiUrl()}/admin/risk-model/${encodeURIComponent(selectedRiskModelCountry)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editedRiskModel)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`Risk model saved for ${selectedRiskModelCountry}:`, result);
        setIsEditingRiskModel(false);
        // Refresh the risk model content
        await openRiskModelModal(selectedRiskModelCountry);
      } else {
        const error = await response.json();
        alert(`Failed to save risk model: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error saving risk model:', error);
      alert(`Error saving risk model: ${error}`);
    } finally {
      setSavingRiskModel(false);
    }
  };

  // Helper function to format risk values
  const formatRisk = (risk: number | undefined, currency: string) => {
    if (risk === undefined || risk === null) return 'N/A';
    // Format the value as number with locale formatting, then append the currency code
    let formatted = risk.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return `${formatted} ${currency || ''}`.trim();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg max-w-md">
            <h2 className="text-lg font-semibold mb-2">Error</h2>
            <p>{error}</p>
            <button 
              onClick={fetchCountries}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Country Research Admin</h1>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-gray-600">Total Countries</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-green-600">{stats.researched}</div>
              <div className="text-gray-600">Researched</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
              <div className="text-gray-600">Pending</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-2xl font-bold text-purple-600">{stats.risk_models_available}</div>
              <div className="text-gray-600">Risk Models</div>
            </div>
          </div>
        </div>

        {/* Countries Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Country Research Status</h2>
          </div>
          
          <div className="overflow-x-auto">
            {countries && countries.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {Object.entries(groupCountriesByRegion(countries)).map(([region, regionCountries]) => (
                    <div key={region} className="border-b border-gray-200">
                      {/* Region Header */}
                      <div 
                        className="px-6 py-4 bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                        onClick={() => toggleRegion(region)}
                      >
                        <div className="flex items-center space-x-3">
                          <svg 
                            className={`w-5 h-5 transition-transform ${collapsedRegions[region] ? '' : 'rotate-90'}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <h3 className="text-lg font-medium text-gray-900">{region}</h3>
                          <span className="text-sm text-gray-500">({regionCountries.length} countries)</span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>Researched: {regionCountries.filter(c => c.research_available).length}</span>
                          <span>Pending: {regionCountries.filter(c => !c.research_available).length}</span>
                        </div>
                      </div>
                      
                      {/* Region Countries */}
                      {!collapsedRegions[region] && (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Country
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Research
                              </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Risk Model
                            </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Update
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Risk
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {regionCountries.map((country) => (
                              <tr key={country.country} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {country.country}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {country.research_available ? (
                                  <button
                                    onClick={() => openResearchModal(country.country)}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-colors"
                                  >
                                    Open Research Data
                                  </button>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Research pending
                                  </span>
                                )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {country.risk_model?.model_available ? (
                                  <button
                                    onClick={() => openRiskModelModal(country.country)}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer transition-colors"
                                  >
                                    Open Risk Model
                                  </button>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Risk Model pending
                                  </span>
                                )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(country.last_update)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {/* Status column: show status/progress only */}
                                  {researching === country.country ? (
                                    <div className="flex items-center space-x-2">
                                      <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                                      <span className="text-blue-600 font-medium">
                                        {researchProgress[country.country]?.current_prompt || 'Researching...'}
                                      </span>
                                    <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                                        <div 
                                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                          style={{ width: `${researchProgress[country.country]?.progress || 0}%` }}
                                        ></div>
                                      </div>
                                      <span className="text-xs text-gray-600 min-w-[3rem]">
                                        {researchProgress[country.country]?.progress || 0}%
                                      </span>
                                    </div>
                                ) : generatingRiskModel === country.country ? (
                                  <div className="flex items-center space-x-2">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b border-purple-600"></div>
                                    <span className="text-purple-600 font-medium text-xs">Generating Risk Model...</span>
                                  </div>
                                ) : (
                                  <span className="text-gray-500">Ready</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                {formatRisk(country.risk_of_non_compliance, country.risk_currency)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {/* Actions column: show both buttons as appropriate */}
                                <div className="flex flex-col space-y-2">
                                    <button
                                      onClick={() => researchCountry(country.country)}
                                      disabled={researching === country.country}
                                      className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md ${
                                        researching === country.country
                                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                          : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                      }`}
                                    >
                                      {researching === country.country ? (
                                        <>
                                          <div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-2"></div>
                                          Researching...
                                        </>
                                      ) : (
                                        'Update Research'
                                      )}
                                    </button>
                                  {/* Update Risk Model Button - only show if research is available */}
                                  {country.research_available && (
                                    generatingRiskModel === country.country ? (
                                      <div className="flex items-center space-x-2">
                                        <div className="animate-spin rounded-full h-3 w-3 border-b border-purple-600"></div>
                                        <span className="text-purple-600 font-medium text-xs">Generating...</span>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => generateRiskModel(country.country)}
                                        disabled={generatingRiskModel === country.country}
                                        className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md ${
                                          generatingRiskModel === country.country
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                                        }`}
                                      >
                                        {country.risk_model?.model_available ? 'Update Risk Model' : 'Generate Risk Model'}
                                      </button>
                                    )
                                  )}
                                </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-4 text-center text-gray-500">
                No countries found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Research Modal */}
      {selectedCountry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Research: {selectedCountry}
              </h2>
              <div className="flex items-center space-x-2">
                {activeTab === 'research' && isEditing ? (
                  <>
                    <button
                      onClick={saveResearch}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditedContent(researchContent);
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </>
                ) : activeTab === 'summary' && isEditingSummary ? (
                  <>
                    <button
                      onClick={saveSummary}
                      disabled={savingSummary}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {savingSummary ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingSummary(false);
                        setEditedSummary(summaryData);
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      if (activeTab === 'research') {
                        setIsEditing(true);
                      } else if (activeTab === 'summary' && summaryData) {
                        setIsEditingSummary(true);
                      }
                    }}
                    disabled={activeTab === 'summary' && !summaryData}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('research')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'research'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Research Content
                </button>
                <button
                  onClick={() => setActiveTab('summary')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'summary'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  JSON Summary
                  {summaryData && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Available
                    </span>
                  )}
                </button>
              </nav>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {activeTab === 'research' ? (
                // Research Content Tab
                isEditing ? (
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-full min-h-[400px] p-4 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter research content..."
                  />
                ) : (
                  <div className="prose prose-blue max-w-none bg-gray-50 p-4 rounded-lg max-h-[400px] overflow-auto">
                    <ReactMarkdown>{researchContent}</ReactMarkdown>
                  </div>
                )
              ) : (
                // JSON Summary Tab
                <div className="space-y-6">
                  {!summaryData ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Summary Available</h3>
                      <p className="text-gray-500">The JSON summary has not been generated yet. Run the research process to create a structured summary.</p>
                    </div>
                  ) : isEditingSummary ? (
                    // JSON Summary Editing Form
                    <div className="space-y-6">
                      {/* Mandate Status */}
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Mandate Status</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {['b2b', 'b2c', 'b2g'].map((type) => (
                            <div key={type} className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700 capitalize">
                                {type.toUpperCase()} Status
                              </label>
                              <select
                                value={editedSummary?.mandate_status?.[type] || 'no'}
                                onChange={(e) => setEditedSummary({
                                  ...editedSummary,
                                  mandate_status: {
                                    ...editedSummary.mandate_status,
                                    [type]: e.target.value
                                  }
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              >
                                <option value="implemented">Implemented</option>
                                <option value="planned">Planned</option>
                                <option value="no">No</option>
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Mandate Summaries */}
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Mandate Summaries</h3>
                        <div className="space-y-4">
                          {['b2b', 'b2c', 'b2g'].map((type) => (
                            <div key={type}>
                              <label className="block text-sm font-medium text-gray-700 mb-2 capitalize">
                                {type.toUpperCase()} Summary
                              </label>
                              <textarea
                                value={editedSummary?.mandate_summaries?.[type] || ''}
                                onChange={(e) => setEditedSummary({
                                  ...editedSummary,
                                  mandate_summaries: {
                                    ...editedSummary.mandate_summaries,
                                    [type]: e.target.value
                                  }
                                })}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder={`Enter ${type.toUpperCase()} mandate summary...`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Key Dates */}
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium text-gray-900">Key Dates</h3>
                          <button
                            onClick={() => setEditedSummary({
                              ...editedSummary,
                              key_dates: [
                                ...(editedSummary.key_dates || []),
                                {
                                  date: '',
                                  subject: '',
                                  relevance: [],
                                  summary: ''
                                }
                              ]
                            })}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Add Date
                          </button>
                        </div>
                        <div className="space-y-4">
                          {(editedSummary?.key_dates || []).map((date: any, index: number) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-700">Date {index + 1}</h4>
                                <button
                                  onClick={() => setEditedSummary({
                                    ...editedSummary,
                                    key_dates: editedSummary.key_dates.filter((_: any, i: number) => i !== index)
                                  })}
                                  className="text-red-600 hover:text-red-800 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                                  <input
                                    type="date"
                                    value={date.date || ''}
                                    onChange={(e) => {
                                      const newDates = [...editedSummary.key_dates];
                                      newDates[index] = { ...date, date: e.target.value };
                                      setEditedSummary({ ...editedSummary, key_dates: newDates });
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
                                  <input
                                    type="text"
                                    value={date.subject || ''}
                                    onChange={(e) => {
                                      const newDates = [...editedSummary.key_dates];
                                      newDates[index] = { ...date, subject: e.target.value };
                                      setEditedSummary({ ...editedSummary, key_dates: newDates });
                                    }}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Event title"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Relevance</label>
                                <div className="flex space-x-3">
                                  {['b2b', 'b2c', 'b2g'].map((type) => (
                                    <label key={type} className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={date.relevance?.includes(type) || false}
                                        onChange={(e) => {
                                          const newDates = [...editedSummary.key_dates];
                                          const currentRelevance = date.relevance || [];
                                          const newRelevance = e.target.checked
                                            ? [...currentRelevance, type]
                                            : currentRelevance.filter((r: string) => r !== type);
                                          newDates[index] = { ...date, relevance: newRelevance };
                                          setEditedSummary({ ...editedSummary, key_dates: newDates });
                                        }}
                                        className="mr-1"
                                      />
                                      <span className="text-xs text-gray-700 capitalize">{type}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Summary</label>
                                <textarea
                                  value={date.summary || ''}
                                  onChange={(e) => {
                                    const newDates = [...editedSummary.key_dates];
                                    newDates[index] = { ...date, summary: e.target.value };
                                    setEditedSummary({ ...editedSummary, key_dates: newDates });
                                  }}
                                  rows={2}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="Description of the event"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Sources */}
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium text-gray-900">Sources</h3>
                          <button
                            onClick={() => setEditedSummary({
                              ...editedSummary,
                              sources: [...(editedSummary.sources || []), '']
                            })}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Add Source
                          </button>
                        </div>
                        <div className="space-y-2">
                          {(editedSummary?.sources || []).map((source: string, index: number) => (
                            <div key={index} className="flex items-center space-x-2">
                              <input
                                type="url"
                                value={source}
                                onChange={(e) => {
                                  const newSources = [...editedSummary.sources];
                                  newSources[index] = e.target.value;
                                  setEditedSummary({ ...editedSummary, sources: newSources });
                                }}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="https://example.com"
                              />
                              <button
                                onClick={() => setEditedSummary({
                                  ...editedSummary,
                                  sources: editedSummary.sources.filter((_: string, i: number) => i !== index)
                                })}
                                className="text-red-600 hover:text-red-800 text-sm px-2"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // JSON Summary View Mode
                    <div className="space-y-6">
                      {/* Mandate Status */}
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Mandate Status</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {Object.entries(summaryData.mandate_status || {}).map(([type, status]) => (
                            <div key={type} className="text-center">
                              <div className="text-sm font-medium text-gray-700 capitalize mb-1">{type}</div>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                status === 'implemented' ? 'bg-green-100 text-green-800' :
                                status === 'planned' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {status as string}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Mandate Summaries */}
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Mandate Summaries</h3>
                        <div className="space-y-4">
                          {Object.entries(summaryData.mandate_summaries || {}).map(([type, summary]) => (
                            <div key={type}>
                              <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize">{type.toUpperCase()}</h4>
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{summary as string}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Key Dates */}
                      <div className="bg-white border border-gray-300 rounded-lg p-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Key Dates</h3>
                        <div className="space-y-3">
                          {(summaryData.key_dates || []).map((date: any, index: number) => (
                            <div key={index} className="border border-gray-200 rounded-lg p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900">{date.date}</span>
                                    <span className="text-sm text-gray-600">-</span>
                                    <span className="text-sm font-medium text-gray-900">{date.subject}</span>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">{date.summary}</p>
                                  <div className="flex space-x-2">
                                    {date.relevance?.map((rel: string) => (
                                      <span key={rel} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                        {rel}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Sources */}
                      {summaryData.sources && summaryData.sources.length > 0 && (
                        <div className="bg-white border border-gray-300 rounded-lg p-4">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">Sources</h3>
                          <div className="space-y-2">
                            {summaryData.sources.map((source: string, index: number) => (
                              <a
                                key={index}
                                href={source}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {source}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Risk Model Modal */}
      {selectedRiskModelCountry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Risk Model: {selectedRiskModelCountry}
              </h2>
              <button
                onClick={closeRiskModelModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column: Risk Model Code */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Risk Calculation Model</h3>
                    {editedRiskModel && (
                      <button
                        onClick={() => setIsEditingRiskModel(!isEditingRiskModel)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {isEditingRiskModel ? 'View Raw' : 'Edit Model'}
                      </button>
                    )}
                  </div>
                  
                  {isEditingRiskModel && editedRiskModel ? (
                    <div className="bg-white border border-gray-300 rounded-lg p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cost per Non-Compliant AP Invoice
                        </label>
                        <input
                          type="number"
                          value={editedRiskModel.cost_per_noncompliant_ap_invoice || ''}
                          onChange={(e) => setEditedRiskModel({
                            ...editedRiskModel,
                            cost_per_noncompliant_ap_invoice: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter cost"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cost per Non-Compliant AR Invoice
                        </label>
                        <input
                          type="number"
                          value={editedRiskModel.cost_per_noncompliant_ar_invoice || ''}
                          onChange={(e) => setEditedRiskModel({
                            ...editedRiskModel,
                            cost_per_noncompliant_ar_invoice: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter cost"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confidence Score (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={editedRiskModel.confidence_score || ''}
                          onChange={(e) => setEditedRiskModel({
                            ...editedRiskModel,
                            confidence_score: parseInt(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter confidence score"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Currency Code
                        </label>
                        <input
                          type="text"
                          value={editedRiskModel.currency_code || ''}
                          onChange={(e) => setEditedRiskModel({
                            ...editedRiskModel,
                            currency_code: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="e.g., EUR, USD"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Formula
                        </label>
                        <textarea
                          value={editedRiskModel.formula || ''}
                          onChange={(e) => setEditedRiskModel({
                            ...editedRiskModel,
                            formula: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter calculation formula"
                          rows={2}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Intangible Risks
                        </label>
                        <textarea
                          value={Array.isArray(editedRiskModel.intangible_risks) ? editedRiskModel.intangible_risks.join('\n') : ''}
                          onChange={(e) => setEditedRiskModel({
                            ...editedRiskModel,
                            intangible_risks: e.target.value.split('\n').filter(risk => risk.trim())
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter risks (one per line)"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Explanation
                        </label>
                        <textarea
                          value={editedRiskModel.explanation || ''}
                          onChange={(e) => setEditedRiskModel({
                            ...editedRiskModel,
                            explanation: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter explanation"
                          rows={4}
                        />
                      </div>
                      
                      <div className="flex space-x-3 pt-4">
                        <button
                          onClick={saveRiskModel}
                          disabled={savingRiskModel}
                          className={`flex-1 px-4 py-2 text-white font-medium rounded-md ${
                            savingRiskModel
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                          }`}
                        >
                          {savingRiskModel ? (
                            <div className="flex items-center justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2"></div>
                              Saving...
                            </div>
                          ) : (
                            'Save Changes'
                          )}
                        </button>
                        <button
                          onClick={() => setIsEditingRiskModel(false)}
                          className="flex-1 px-4 py-2 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-auto max-h-[400px]">
                      <pre>{riskModelContent}</pre>
                    </div>
                  )}
                </div>

                {/* Right Column: Test Calculation */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Test Calculation</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          AP Invoice Volume
                        </label>
                        <input
                          type="number"
                          value={apVolume}
                          onChange={(e) => setApVolume(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter AP invoice volume"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          AR Invoice Volume
                        </label>
                        <input
                          type="number"
                          value={arVolume}
                          onChange={(e) => setArVolume(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter AR invoice volume"
                        />
                      </div>
                      <button
                        onClick={testCalculation}
                        disabled={calculating}
                        className={`w-full px-4 py-2 text-white font-medium rounded-md ${
                          calculating
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
                        }`}
                      >
                        {calculating ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b border-white mr-2"></div>
                            Calculating...
                          </div>
                        ) : (
                          'Calculate Risk'
                        )}
                      </button>
                    </div>

                    {/* Calculation Results */}
                    {calculationResult && (
                      <div className="mt-6">
                        <h4 className="text-md font-medium text-gray-900 mb-3">Calculation Results</h4>
                        {calculationResult.error ? (
                          <div className="bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-red-800 text-sm">{calculationResult.error}</p>
                          </div>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded-md p-3 space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-700">Risk Level:</span>
                              <span className={`text-sm font-bold px-2 py-1 rounded ${
                                calculationResult.risk_level === 'High' ? 'bg-red-100 text-red-800' :
                                calculationResult.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {calculationResult.risk_level}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-700">Risk Score:</span>
                              <span className="text-sm text-gray-900">{calculationResult.risk_score}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm font-medium text-gray-700">Estimated Penalties:</span>
                              <span className="text-sm text-gray-900">{calculationResult.estimated_penalties}</span>
                            </div>
                            {calculationResult.compliance_requirements && (
                              <div>
                                <span className="text-sm font-medium text-gray-700 block mb-2">Compliance Requirements:</span>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {calculationResult.compliance_requirements.map((req: any, index: number) => (
                                    <li key={index} className="flex justify-between">
                                      <span>{req.title}</span>
                                      <span className="text-red-600">{req.penalty}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Timeline Section Component
function TimelineSection({ events, countries }: { events: TimelineEvent[], countries: CountryVolume[] }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountries, setSelectedCountries] = useState<string[]>(countries.map(c => c.country));
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const itemsPerPage = 10;

  // Format date according to user's browser settings
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString; // Fallback to original string if parsing fails
    }
  };

  // Filter events by selected countries
  const filteredEvents = events.filter(event => 
    selectedCountries.includes(event.country)
  );

  // Debug logging
  console.log('TimelineSection Debug:', {
    totalEvents: events.length,
    selectedCountries,
    filteredEvents: filteredEvents.length,
    events: events.map(e => ({ country: e.country, title: e.title })),
    countries: countries.map(c => c.country)
  });

  // Pagination
  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEvents = filteredEvents.slice(startIndex, endIndex);

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType) {
      case 'deadline':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'implementation':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div>
      {/* Country Filter */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Filter by Country</h3>
        <div className="flex flex-wrap gap-2">
          {countries.map(country => {
            const isSelected = selectedCountries.includes(country.country);
            return (
              <label key={country.country} className="flex items-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCountries([...selectedCountries, country.country]);
                    } else {
                      setSelectedCountries(selectedCountries.filter(c => c !== country.country));
                    }
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                  className="sr-only" // Hide the checkbox visually but keep it accessible
                />
                <span className={`text-xs px-3 py-1 rounded-full border transition-colors cursor-pointer ${
                  isSelected 
                    ? 'bg-blue-100 text-blue-800 border-blue-300 font-medium' 
                    : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                }`}>
                  {country.country}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Timeline Events */}
      <div className="space-y-4">
        {currentEvents.map((event, index) => (
          <div 
            key={index} 
            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedEvent(event)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                {getEventTypeIcon(event.event_type)}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {formatDate(event.date) ? `${formatDate(event.date)}: ${event.title}` : event.title}
                    </h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(event.priority)}`}>
                      {event.priority}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-2">{event.description}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {event.country}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredEvents.length)} of {filteredEvents.length} events
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Key Date Detail Modal */}
      <KeyDateDetailModal 
        event={selectedEvent} 
        onClose={() => setSelectedEvent(null)} 
      />
    </div>
  );
}

// Key Date Detail Modal Component
function KeyDateDetailModal({ event, onClose }: { event: TimelineEvent | null, onClose: () => void }) {
  if (!event) return null;

  // Format date according to user's browser settings
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const getRelevanceColor = (type: string) => {
    switch (type) {
      case 'b2b': return 'bg-orange-100 text-orange-800';
      case 'b2c': return 'bg-teal-100 text-teal-800';
      case 'b2g': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRelevanceText = (type: string) => {
    switch (type) {
      case 'b2b': return 'B2B';
      case 'b2c': return 'B2C';
      case 'b2g': return 'B2G';
      default: return type.toUpperCase();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {formatDate(event.date)}: {event.title}
              </h2>
              <p className="text-sm text-gray-600">{event.country}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            {/* Event Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Event Details</h3>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    event.priority === 'high' ? 'bg-red-100 text-red-800' :
                    event.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {event.priority.charAt(0).toUpperCase() + event.priority.slice(1)} Priority
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                  </span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <p className="text-gray-900 font-medium">{formatDate(event.date)}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <p className="text-gray-900">{event.title}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-gray-900">{event.description}</p>
                </div>
                
                {event.relevance && event.relevance.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Relevance</label>
                    <div className="flex flex-wrap gap-2">
                      {event.relevance.map((type, index) => (
                        <span
                          key={index}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getRelevanceColor(type)}`}
                        >
                          {getRelevanceText(type)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Detailed Summary */}
            {event.summary && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Detailed Summary</h3>
                <p className="text-gray-700 leading-relaxed">{event.summary}</p>
              </div>
            )}

            {/* Sources */}
            {event.sources && event.sources.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Sources</h3>
                <div className="space-y-2">
                  {event.sources.map((source, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <a
                        href={source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm break-all"
                      >
                        {source}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Country Operations Section Component
function CountryOperationsSection({ operations }: { operations: CountryOperation[] }) {
  const [selectedCountry, setSelectedCountry] = useState<CountryOperation | null>(null);
  const [riskModel, setRiskModel] = useState<any>(null);
  const [riskModelContent, setRiskModelContent] = useState<string>('');
  const [apVolume, setApVolume] = useState('1000');
  const [arVolume, setArVolume] = useState('1000');
  const [calculationResult, setCalculationResult] = useState<any>(null);
  const [calculating, setCalculating] = useState(false);
  const [loadingModel, setLoadingModel] = useState(false);
  const [countryResearch, setCountryResearch] = useState<any>(null);

  const getMandateStatusColor = (status: string) => {
    switch (status) {
      case 'implemented': return 'bg-red-100 text-red-800'; // Implemented is now red
      case 'planned': return 'bg-yellow-100 text-yellow-800';
      case 'no': return 'bg-green-100 text-green-800'; // No mandate is now green
      case 'unknown': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMandateStatusText = (status: string) => {
    switch (status) {
      case 'implemented': return 'Implemented';
      case 'planned': return 'Planned';
      case 'no': return 'No mandate'; // Replace 'No' with 'No mandate'
      case 'unknown': return 'Unknown';
      default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
    }
  };

  const formatRisk = (risk: number | undefined, currency: string) => {
    if (risk === undefined || risk === null) return 'N/A';
    let formatted = risk.toLocaleString(undefined, { maximumFractionDigits: 0 });
    return `${formatted} ${currency || ''}`.trim();
  };

  // Fetch risk model when modal opens
  useEffect(() => {
    if (selectedCountry) {
      setLoadingModel(true);
      fetch(`${getApiUrl()}/admin/risk-model/${encodeURIComponent(selectedCountry.country)}`)
        .then(res => res.json())
        .then(data => {
          setRiskModelContent(data.model_code || 'No model code available');
          try {
            const jsonMatch = data.model_code?.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              setRiskModel(JSON.parse(jsonMatch[0]));
            } else {
              setRiskModel(null);
            }
          } catch {
            setRiskModel(null);
          }
        })
        .finally(() => setLoadingModel(false));
      // Fetch country research summary from backend API
      fetch(`${getApiUrl()}/admin/research/${encodeURIComponent(selectedCountry.country)}/summary`)
        .then(res => res.ok ? res.json() : null)
        .then(data => setCountryResearch(data))
        .catch(() => setCountryResearch(null));
    } else {
      setRiskModel(null);
      setRiskModelContent('');
      setCalculationResult(null);
      setApVolume('1000');
      setArVolume('1000');
      setCountryResearch(null);
    }
  }, [selectedCountry]);

  const testCalculation = async () => {
    if (!selectedCountry) return;
    setCalculating(true);
    try {
      const response = await fetch(`/api/calculate-compliance-risk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          country: selectedCountry.country,
          ap_invoices: parseInt(apVolume) || 0,
          ar_invoices: parseInt(arVolume) || 0
        })
      });
      if (response.ok) {
        setCalculationResult(await response.json());
      } else {
        setCalculationResult({ error: 'Calculation failed' });
      }
    } catch (error) {
      setCalculationResult({ error: `Error: ${error}` });
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Country
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              AP Volume
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              AR Volume
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              B2B Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              B2G Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              B2C Status
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Risk
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {operations.map((operation, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 cursor-pointer underline" onClick={() => setSelectedCountry(operation)}>
                {operation.country}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {operation.ap_volume.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {operation.ar_volume.toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMandateStatusColor(operation.mandate_status.b2b)}`}>
                  {getMandateStatusText(operation.mandate_status.b2b)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMandateStatusColor(operation.mandate_status.b2g)}`}>
                  {getMandateStatusText(operation.mandate_status.b2g)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMandateStatusColor(operation.mandate_status.b2c)}`}>
                  {getMandateStatusText(operation.mandate_status.b2c)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                {formatRisk(operation.risk_of_non_compliance, operation.risk_currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Country Detail Modal */}
      {selectedCountry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedCountry.country} — Country Research Details
              </h2>
              <button onClick={() => setSelectedCountry(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {!countryResearch ? (
                <div className="text-center py-8 text-gray-500">Loading country research...</div>
              ) : (
                (() => {
                  const summary = countryResearch.summary || {};
                  return (
                    <>
                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Mandate Summaries</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="font-semibold mb-1">B2B</div>
                            <div className={`mb-2 px-2 py-1 text-xs font-medium rounded-full inline-block ${getMandateStatusColor(summary.mandate_status?.b2b)}`}>{getMandateStatusText(summary.mandate_status?.b2b)}</div>
                            <div className="text-sm text-gray-800">{summary.mandate_summaries?.b2b || 'N/A'}</div>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="font-semibold mb-1">B2C</div>
                            <div className={`mb-2 px-2 py-1 text-xs font-medium rounded-full inline-block ${getMandateStatusColor(summary.mandate_status?.b2c)}`}>{getMandateStatusText(summary.mandate_status?.b2c)}</div>
                            <div className="text-sm text-gray-800">{summary.mandate_summaries?.b2c || 'N/A'}</div>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="font-semibold mb-1">B2G</div>
                            <div className={`mb-2 px-2 py-1 text-xs font-medium rounded-full inline-block ${getMandateStatusColor(summary.mandate_status?.b2g)}`}>{getMandateStatusText(summary.mandate_status?.b2g)}</div>
                            <div className="text-sm text-gray-800">{summary.mandate_summaries?.b2g || 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Key Dates / Timeline</h3>
                        <ul className="space-y-2">
                          {(summary.key_dates?.length ? [...summary.key_dates].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((event: any, idx: number) => {
                            const isPast = new Date(event.date) < new Date();
                            return (
                              <li key={idx} className="border rounded p-2 bg-gray-50">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${isPast ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{isPast ? 'Historic' : 'Upcoming'}</span>
                                  <span className="font-semibold">{new Date(event.date).toLocaleDateString()} — {event.subject}</span>
                                </div>
                                <div className="text-gray-700 text-sm mb-1">{event.summary}</div>
                                <div className="text-xs text-gray-500">Relevance: {event.relevance?.join(', ') || 'N/A'}</div>
                              </li>
                            );
                          }) : <li>No key dates available.</li>)}
                        </ul>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Sources</h3>
                        <ul className="list-disc list-inside text-blue-700 text-sm">
                          {summary.sources?.length ? summary.sources.map((src: string, idx: number) => (
                            <li key={idx}>
                              <a href={src} target="_blank" rel="noopener noreferrer" className="underline">
                                {src.length > 75 ? src.slice(0, 75) + '...' : src}
                              </a>
                            </li>
                          )) : <li>No sources available.</li>}
                        </ul>
                      </div>
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main App Component
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<CompanyOnboarding />} />
        <Route path="/volumes" element={<VolumeInput />} />
        <Route path="/report/:reportCode" element={<ReportPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </Router>
  );
}

export default App;